import { useState, useRef, useEffect, memo } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlayIcon, PauseIcon } from "@hugeicons-pro/core-solid-rounded";
import { cx } from "shared/utils";

const WAVEFORM_BARS = [
  15, 25, 20, 45, 30, 55, 25, 40, 60, 20, 35, 50, 25, 65, 30, 20, 40, 55, 25,
  70, 35, 20, 45, 30, 55, 20, 40, 65, 25, 50, 35, 20, 75, 30, 45, 25, 55, 20,
  40, 60, 30, 25, 50, 35, 20, 45, 70, 25, 55, 30, 40, 20, 60, 35, 25, 45, 20,
  50, 65, 30, 25, 40, 55, 20, 35, 75, 25, 45, 30, 60, 20, 50, 35, 25, 40, 55,
  20, 65, 30, 45, 25, 50, 20, 35, 60, 25, 70, 40, 20, 55, 30, 45, 25, 20, 50,
  35, 65, 25, 40, 20, 55, 30, 45, 75, 20, 35, 50, 25, 60, 20, 40, 30, 55, 25,
  45, 20, 65, 35, 50, 25, 30, 40, 20, 55, 70, 25, 40, 55, 20, 35, 60, 25, 45,
  30, 70, 20, 50, 35, 25, 55, 40, 65, 20, 30, 45, 25, 50, 75, 20, 40, 35, 55,
  25, 60, 30, 20, 45, 50, 25, 65, 35, 40, 20, 55, 70, 30, 25, 45, 20, 50, 35,
  60, 25, 40, 75, 20, 55, 30, 45, 25, 35, 50, 20, 65, 40, 25, 55, 30, 45, 20,
  60, 35, 70, 25, 50, 40, 20, 55, 25, 45, 30, 65, 20, 35, 50, 40, 25, 55, 75,
  20, 30, 45, 60, 25, 35, 50, 40, 20, 55, 25, 70, 30, 45, 35, 20, 60, 50, 25,
  40, 55, 65, 20, 35, 30, 45, 25, 50, 20, 55, 40, 30, 65, 25, 35, 70, 45, 20,
  50, 60, 25, 40, 55
];

const formatTime = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0)
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

let activeAudioPlayer: { pause: () => void; reset: () => void } | null = null;

const WaveformBar = ({ h, className }: { h: number; className: string }) => (
  <div
    className={cx("w-px shrink-0 rounded-full", className)}
    style={{ height: `${h}%` }}
  />
);

const WaveformBars = memo(function WaveformBars({
  waveformRef
}: {
  waveformRef: React.RefObject<HTMLDivElement>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    // eslint-disable-next-line compat/compat -- ResizeObserver is supported in all target browsers
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="relative flex-1 h-9 overflow-hidden">
      <div className="absolute inset-0 flex items-center gap-[1.5px]">
        {WAVEFORM_BARS.map((h, i) => (
          <WaveformBar key={i} h={h} className="bg-alpha-100" />
        ))}
      </div>
      <div
        ref={waveformRef}
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: "0%" }}
      >
        <div
          className="flex items-center gap-[1.5px] h-full"
          style={{ width: containerWidth || "100%" }}
        >
          {WAVEFORM_BARS.map((h, i) => (
            <WaveformBar key={i} h={h} className="bg-primary-500" />
          ))}
        </div>
      </div>
    </div>
  );
});

interface CallRecordingPlayerProps {
  recordingUrl: string;
  durationSeconds?: number | null;
  disabled?: boolean;
  // When false, playback is stopped and rewound. Lets a parent that keeps this
  // mounted (e.g. a drawer that only toggles `open`) halt audio on close.
  active?: boolean;
}

export const CallRecordingPlayer = ({
  recordingUrl,
  durationSeconds,
  disabled = false,
  active = true
}: CallRecordingPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  // Authoritative duration from the loaded audio metadata; falls back to the
  // prop until it loads. Derived (not synced via effect) to avoid prop/state drift.
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const duration = Math.max(audioDuration ?? durationSeconds ?? 0, 1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef<number>(0);
  const waveformRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const isPlayingRef = useRef(false);

  useEffect(() => {
    const updateProgress = (audio: HTMLAudioElement) => {
      const t = audio.currentTime;
      const d = Math.max(audio.duration || duration, 1);
      const pct = (t / d) * 100;
      if (waveformRef.current) waveformRef.current.style.width = `${pct}%`;
      if (timeRef.current)
        timeRef.current.textContent = `${formatTime(t)}/${formatTime(d)}`;
    };

    const resetProgress = (audio: HTMLAudioElement) => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      if (waveformRef.current) waveformRef.current.style.width = "0%";
      if (timeRef.current)
        timeRef.current.textContent = `0:00/${formatTime(Math.max(audio.duration || duration, 1))}`;
    };

    const tick = () => {
      const audio = audioRef.current;
      if (audio && isPlayingRef.current)
        audio.ended ? resetProgress(audio) : updateProgress(audio);

      // eslint-disable-next-line compat/compat -- requestAnimationFrame is supported in all target browsers
      rafRef.current = requestAnimationFrame(tick);
    };
    // eslint-disable-next-line compat/compat -- requestAnimationFrame is supported in all target browsers
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration]);

  // Clear stale error state whenever the source changes. The drawer mounts this
  // player with an empty URL while the details query loads, which fires a spurious
  // `error` event; without this reset it would stick even after the real URL arrives.
  useEffect(() => {
    setAudioError(false);
  }, [recordingUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoaded = () => {
      if (audio.duration !== Infinity) setAudioDuration(audio.duration);
    };
    const onEnded = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      if (waveformRef.current) waveformRef.current.style.width = "0%";
      if (timeRef.current)
        timeRef.current.textContent = `0:00/${formatTime(Math.max(duration, 1))}`;
    };
    const onError = () => {
      setAudioError(true);
      setIsPlaying(false);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [recordingUrl, duration]);

  const resetPlayer = () => {
    if (audioRef.current) audioRef.current.pause();
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (waveformRef.current) waveformRef.current.style.width = "0%";
    if (timeRef.current)
      timeRef.current.textContent = `0:00/${formatTime(Math.max(duration, 1))}`;
  };

  // Stop and rewind when the player is deactivated (e.g. the drawer closes) so
  // audio doesn't keep playing in the background.
  useEffect(() => {
    if (active) return;
    if (audioRef.current) audioRef.current.pause();
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (waveformRef.current) waveformRef.current.style.width = "0%";
    if (timeRef.current)
      timeRef.current.textContent = `0:00/${formatTime(Math.max(duration, 1))}`;
  }, [active, duration]);

  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      isPlayingRef.current = false;
      setIsPlaying(false);
      activeAudioPlayer = null;
    } else {
      if (activeAudioPlayer) activeAudioPlayer.pause();

      audioRef.current
        .play()
        .then(() => {
          isPlayingRef.current = true;
          setIsPlaying(true);
          activeAudioPlayer = {
            pause: () => {
              audioRef.current?.pause();
              isPlayingRef.current = false;
              setIsPlaying(false);
            },
            reset: resetPlayer
          };
        })
        .catch(() => {
          isPlayingRef.current = false;
          setIsPlaying(false);
        });
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const timeWidth = 60;
    const barWidth = rect.width - timeWidth;
    const x = Math.max(0, Math.min(e.clientX - rect.left, barWidth));
    audioRef.current.currentTime = (x / barWidth) * duration;
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={togglePlayback}
          disabled={disabled || audioError}
          className="flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <HugeiconsIcon icon={isPlaying ? PauseIcon : PlayIcon} size={16} />
        </button>
        <div
          className="flex flex-1 items-center gap-2 cursor-pointer"
          onClick={handleSeek}
        >
          <WaveformBars waveformRef={waveformRef} />
          <span
            ref={timeRef}
            className="flex-shrink-0 text-xs w-[60px] text-right font-medium"
          >
            0:00/{formatTime(Math.max(duration, 1))}
          </span>
        </div>
      </div>
      <audio
        ref={audioRef}
        src={recordingUrl || undefined}
        preload="metadata"
      />
      {audioError && (
        <p className="mt-1 text-xs text-danger-500">Recording unavailable</p>
      )}
    </div>
  );
};
